import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, X, Loader2, ArrowLeft, RefreshCw, ChevronDown, ChevronUp, Upload, Sparkles, History, Download } from 'lucide-react';
import { productAPI, fetchPaged, masterChangeAPI } from '../../services/api';
import BulkImportWizard from './BulkImportWizard';
import Pagination from '../ui/Pagination';
import ItemHistoryDialog from '../ui/ItemHistoryDialog';
import { exportToCSV } from '../../utils/exportCSV';
import { toast } from 'sonner';

const categories = ['Finished Goods', 'Raw Materials', 'Trading Goods', 'Consumables', 'Assets', 'Service', 'Uncategorized'];
const units = ['kg', 'PCS', 'Mtr', 'Sqft', 'Sqm', 'Ltr', 'Roll', 'Drum', 'Set', 'Pkt', 'nos', 'feet'];

const emptyForm = {
  name: '', sku: '', category: '', sub_category: '', type: 'Finished Good',
  unit: 'kg', buy_unit: '', use_unit: '', sell_unit: '',
  cost_price: 0, selling_price: 0, hsn_code: '', gst_rate: 18,
  thickness: '', size: '', material: '', grade: '', methodology: '',
  min_stock: 0, reorder_level: 0, unit_conversions: [],
};

const emptyConversion = { from_unit: '', to_unit: '', factor: 0, description: '' };

const ProductMaster = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState('list');
  const [showImport, setShowImport] = useState(false);
  const [hsnLoading, setHsnLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [showConversions, setShowConversions] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [historyProduct, setHistoryProduct] = useState(null); // { id, name }
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]); // Bulk-edit selection
  const [showBulkSubCat, setShowBulkSubCat] = useState(false);
  const [bulkSubCatValue, setBulkSubCatValue] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [subCategorySuggestions, setSubCategorySuggestions] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const isAdmin = currentUser.role === 'Admin';
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };

  useEffect(() => { fetchProducts(); }, [page, searchDebounce, catFilter, pageSize]);

  // Clear selection when filters/page change to avoid acting on hidden rows
  useEffect(() => { setSelectedIds([]); }, [page, searchDebounce, catFilter, pageSize]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (searchDebounce) params.search = searchDebounce;
      if (catFilter !== 'All') params.category = catFilter;
      const result = await fetchPaged('/products', params);
      setProducts(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.sku || !form.category) {
      alert('Name, SKU, and Category are required');
      return;
    }
    // HSN code must be at least 6 digits
    const hsnDigits = (form.hsn_code || '').replace(/\D/g, '');
    if (form.hsn_code && hsnDigits.length < 6) {
      alert('HSN code must be at least 6 digits');
      return;
    }
    try {
      setSubmitting(true);
      if (editId) {
        const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
        if (user.role === 'Admin') {
          // Admin edits products directly — no approval needed
          await productAPI.update(editId, form);
          toast.success('Product updated successfully');
        } else {
          await masterChangeAPI.create({ entity_type: 'product', entity_id: editId, entity_name: form.name, action: 'edit', changes: form, reason: 'Product update request' });
          toast.success('Edit request submitted for Admin approval');
        }
      } else {
        await productAPI.create(form);
        toast.success('Product created');
      }
      setView('list');
      setForm({ ...emptyForm });
      setEditId(null);
      setPage(1);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    } finally { setSubmitting(false); }
  };

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name || '', sku: p.sku || '', category: p.category || '', sub_category: p.sub_category || '',
      type: p.type || 'Finished Good', unit: p.unit || 'kg',
      buy_unit: p.buy_unit || '', use_unit: p.use_unit || '', sell_unit: p.sell_unit || '',
      cost_price: p.cost_price || 0, selling_price: p.selling_price || 0,
      hsn_code: p.hsn_code || '', gst_rate: p.gst_rate || 18,
      thickness: p.thickness || '', size: p.size || '', material: p.material || '',
      grade: p.grade || '', methodology: p.methodology || '',
      min_stock: p.min_stock || 0, reorder_level: p.reorder_level || 0,
      unit_conversions: p.unit_conversions || [],
    });
    setShowConversions((p.unit_conversions || []).length > 0);
    setView('form');
  };

  const handleDelete = async (id, name) => {
    const reason = window.prompt('Reason for deletion request:');
    if (reason === null) return;
    try {
      await masterChangeAPI.create({ entity_type: 'product', entity_id: id, entity_name: name || id, action: 'delete', reason });
      toast.success('Delete request submitted for Admin approval');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit delete request'); }
  };

  const addConversion = () => {
    setForm({ ...form, unit_conversions: [...form.unit_conversions, { ...emptyConversion }] });
    setShowConversions(true);
  };

  const updateConversion = (idx, key, val) => {
    const convs = [...form.unit_conversions];
    convs[idx] = { ...convs[idx], [key]: key === 'factor' ? Number(val) : val };
    setForm({ ...form, unit_conversions: convs });
  };

  const removeConversion = (idx) => {
    setForm({ ...form, unit_conversions: form.unit_conversions.filter((_, i) => i !== idx) });
  };

  // ── Bulk selection helpers ──
  const allVisibleSelected = products.length > 0 && products.every(p => selectedIds.includes(p.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(products.map(p => p.id));
      setSelectedIds(selectedIds.filter(id => !visibleIds.has(id)));
    } else {
      const next = new Set(selectedIds);
      products.forEach(p => next.add(p.id));
      setSelectedIds([...next]);
    }
  };
  const toggleRow = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const openBulkSubCatDialog = async () => {
    try {
      const rows = await productAPI.listSubCategories();
      setSubCategorySuggestions(rows || []);
    } catch (err) { setSubCategorySuggestions([]); }
    setBulkSubCatValue('');
    setShowBulkSubCat(true);
  };
  const handleBulkSubCatApply = async () => {
    if (selectedIds.length === 0) return;
    try {
      setBulkSubmitting(true);
      const res = await productAPI.bulkUpdateSubCategory(selectedIds, bulkSubCatValue.trim());
      toast.success(`Updated ${res.modified} of ${selectedIds.length} items`);
      setShowBulkSubCat(false);
      setSelectedIds([]);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    } finally { setBulkSubmitting(false); }
  };

  const handleExportProducts = async () => {    try {
      setExporting(true);
      let allProducts = [];
      let pg = 1;
      while (true) {
        const result = await fetchPaged('/products', { page: pg, page_size: 200 });
        allProducts = allProducts.concat(result.items);
        if (!result.has_next) break;
        pg++;
      }
      exportToCSV(allProducts, [
        { label: 'Name', accessor: 'name' },
        { label: 'SKU', accessor: 'sku' },
        { label: 'HSN Code', accessor: 'hsn_code' },
        { label: 'Unit', accessor: 'unit' },
        { label: 'Buy Unit', accessor: 'buy_unit' },
        { label: 'Sell Unit', accessor: 'sell_unit' },
        { label: 'Category', accessor: 'category' },
        { label: 'Sub Category', accessor: 'sub_category' },
        { label: 'Type', accessor: 'type' },
        { label: 'Cost Price', accessor: 'cost_price' },
        { label: 'Selling Price', accessor: 'selling_price' },
        { label: 'GST Rate', accessor: 'gst_rate' },
        { label: 'Min Stock', accessor: 'min_stock' },
        { label: 'Reorder Level', accessor: 'reorder_level' },
        { label: 'Thickness', accessor: 'thickness' },
        { label: 'Size', accessor: 'size' },
        { label: 'Material', accessor: 'material' },
        { label: 'Grade', accessor: 'grade' },
      ], 'item_master');
      toast.success(`Exported ${allProducts.length} products`);
    } catch (err) { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  if (loading && products.length === 0) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} />
      <span className="ml-2 text-sm text-gray-500">Loading products...</span>
    </div>
  );

  // ===== IMPORT VIEW =====
  if (showImport) {
    return (
      <div className="p-5 lg:p-6">
        <BulkImportWizard
          onBack={() => setShowImport(false)}
          onComplete={() => { setShowImport(false); fetchProducts(); }}
        />
      </div>
    );
  }

  // ===== FORM VIEW =====
  if (view === 'form') {
    return (
      <div className="p-5 lg:p-6" data-testid="product-form">
        <button data-testid="back-to-list" onClick={() => { setView('list'); setForm({ ...emptyForm }); setEditId(null); }} className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Products
        </button>

        <div className="bg-white rounded-xl border border-gray-100 p-5 max-w-4xl">
          <h2 className="text-base font-bold text-gray-900 mb-4" style={{ fontFamily: 'DM Sans' }}>
            {editId ? 'Edit Product' : 'New Product'}
          </h2>

          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Product Name *</label>
                <input data-testid="product-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., 25x25x3mm MS Angle" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">SKU *</label>
                <input data-testid="product-sku" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} placeholder="e.g., FAB-MSA-001" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Type</label>
                <select data-testid="product-type" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  {['Raw Material', 'Finished Good', 'Semi-Finished', 'Consumable'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Category */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Category *</label>
                <select data-testid="product-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  <option value="">Select</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Sub-category</label>
                <input value={form.sub_category} onChange={e => setForm({ ...form, sub_category: e.target.value })} placeholder="e.g., Cold Plastic, Hot Dip" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">HSN Code</label>
                <div className="flex gap-1.5">
                  <input data-testid="product-hsn" value={form.hsn_code} onChange={e => setForm({ ...form, hsn_code: e.target.value })} placeholder="e.g., 72161000" className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                  {form.name && !form.hsn_code && (
                    <button data-testid="hsn-ai-lookup" onClick={async () => {
                      try {
                        setHsnLoading(true);
                        const res = await productAPI.hsnLookup([form.name]);
                        if (res.results?.[0]?.hsn_code) setForm(prev => ({ ...prev, hsn_code: res.results[0].hsn_code }));
                      } catch (err) { console.error('HSN lookup failed:', err); }
                      finally { setHsnLoading(false); }
                    }} disabled={hsnLoading}
                      className="flex items-center gap-1 px-2.5 py-2 text-[10px] font-semibold text-white rounded-lg disabled:opacity-50 whitespace-nowrap" style={{ backgroundColor: '#24549c' }}>
                      {hsnLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Specs: Thickness, Size, Material, Grade */}
            <div>
              <p className="text-[11px] font-semibold text-gray-900 mb-2">Specifications</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Thickness</label>
                  <input data-testid="product-thickness" value={form.thickness} onChange={e => setForm({ ...form, thickness: e.target.value })} placeholder="e.g., 3mm" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Size</label>
                  <input data-testid="product-size" value={form.size} onChange={e => setForm({ ...form, size: e.target.value })} placeholder="e.g., 8x4 ft, 25x25mm" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Material</label>
                  <input data-testid="product-material" value={form.material} onChange={e => setForm({ ...form, material: e.target.value })} placeholder="e.g., MS, Aluminium" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Grade</label>
                  <input data-testid="product-grade" value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })} placeholder="e.g., IS 2062, Class I" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Methodology</label>
                  <input value={form.methodology} onChange={e => setForm({ ...form, methodology: e.target.value })} placeholder="e.g., Hot Rolled" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div>
              <p className="text-[11px] font-semibold text-gray-900 mb-2">Pricing & Tax</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Cost Price</label>
                  <input data-testid="product-cost-price" type="number" value={form.cost_price} onChange={e => setForm({ ...form, cost_price: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Selling Price</label>
                  <input data-testid="product-selling-price" type="number" value={form.selling_price} onChange={e => setForm({ ...form, selling_price: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">GST Rate (%)</label>
                  <select data-testid="product-gst" value={form.gst_rate} onChange={e => setForm({ ...form, gst_rate: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                    {[0, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Margin</label>
                  <div className="px-3 py-2 text-xs bg-gray-50 rounded-lg font-semibold" style={{ color: form.selling_price > form.cost_price ? '#16a34a' : '#dc2626' }}>
                    {form.cost_price > 0 ? `${(((form.selling_price - form.cost_price) / form.cost_price) * 100).toFixed(1)}%` : '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* Units */}
            <div>
              <p className="text-[11px] font-semibold text-gray-900 mb-2">Units</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Base Unit</label>
                  <select data-testid="product-unit" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                    {units.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Buy Unit</label>
                  <select data-testid="product-buy-unit" value={form.buy_unit} onChange={e => setForm({ ...form, buy_unit: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                    <option value="">Same as base</option>
                    {units.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Use Unit</label>
                  <select value={form.use_unit} onChange={e => setForm({ ...form, use_unit: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                    <option value="">Same as base</option>
                    {units.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Sell Unit</label>
                  <select data-testid="product-sell-unit" value={form.sell_unit} onChange={e => setForm({ ...form, sell_unit: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                    <option value="">Same as base</option>
                    {units.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Stock Levels */}
            <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Min Stock Level</label>
                <input type="number" value={form.min_stock} onChange={e => setForm({ ...form, min_stock: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Reorder Level</label>
                <input type="number" value={form.reorder_level} onChange={e => setForm({ ...form, reorder_level: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
            </div>

            {/* Unit Conversions */}
            <div className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <button onClick={() => setShowConversions(!showConversions)} className="flex items-center gap-1 text-xs font-semibold text-gray-900">
                  Unit Conversions ({form.unit_conversions.length})
                  {showConversions ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                <button data-testid="add-conversion" onClick={addConversion} className="text-[10px] font-semibold px-2 py-1 rounded-md hover:bg-blue-50" style={{ color: '#24549c' }}>+ Add</button>
              </div>

              {showConversions && form.unit_conversions.length > 0 && (
                <div className="space-y-2">
                  {form.unit_conversions.map((conv, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                      <span className="text-[10px] text-gray-500 font-semibold w-3">{idx + 1}.</span>
                      <span className="text-[10px] text-gray-600">1</span>
                      <select value={conv.from_unit} onChange={e => updateConversion(idx, 'from_unit', e.target.value)} className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded-md">
                        <option value="">From</option>
                        {units.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <span className="text-[10px] text-gray-600">=</span>
                      <input type="number" step="0.01" value={conv.factor} onChange={e => updateConversion(idx, 'factor', e.target.value)} placeholder="Factor" className="w-20 px-2 py-1 text-xs border border-gray-200 rounded-md" />
                      <select value={conv.to_unit} onChange={e => updateConversion(idx, 'to_unit', e.target.value)} className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded-md">
                        <option value="">To</option>
                        {units.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <input value={conv.description || ''} onChange={e => updateConversion(idx, 'description', e.target.value)} placeholder="Note" className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded-md" />
                      <button onClick={() => removeConversion(idx)} className="p-0.5 rounded hover:bg-red-50"><X className="w-3 h-3 text-red-400" /></button>
                    </div>
                  ))}
                  <p className="text-[10px] text-gray-400 mt-1">Example: 1 pcs = 6 meters (for MS Angle 6m length)</p>
                </div>
              )}

              {showConversions && form.unit_conversions.length === 0 && (
                <p className="text-[10px] text-gray-400 text-center py-3">No conversions yet. Click "+ Add" to define how units relate to each other.</p>
              )}
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
              <button onClick={() => { setView('list'); setForm({ ...emptyForm }); setEditId(null); }} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button data-testid="save-product-btn" onClick={handleSubmit} disabled={submitting} className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
                {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {editId ? 'Update Product' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== LIST VIEW =====
  return (
    <div className="p-5 lg:p-6" data-testid="product-master">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Product Master</h1>
          <p className="text-xs text-gray-500 mt-0.5">{total} products | Manage items, specs, pricing & unit conversions</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchProducts} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"><RefreshCw className="w-4 h-4" /></button>
          <button data-testid="export-products-btn" onClick={handleExportProducts} disabled={exporting}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export
          </button>
          <button data-testid="bulk-import-btn" onClick={() => setShowImport(true)} className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            <Upload className="w-3.5 h-3.5" /> Bulk Import
          </button>
          <button data-testid="new-product-btn" onClick={() => { setView('form'); setEditId(null); setForm({ ...emptyForm }); }} className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg" style={{ backgroundColor: '#24549c' }}>
            <Plus className="w-3.5 h-3.5" /> New Product
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="product-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or SKU..." className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <div className="flex gap-1.5">
          {['All', ...categories].map(c => (
            <button key={c} onClick={() => { setCatFilter(c); setPage(1); }} className={`px-3 py-1.5 text-[10px] font-semibold rounded-full transition-all ${catFilter === c ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`} style={catFilter === c ? { backgroundColor: '#24549c' } : {}}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              {isAdmin && (
                <th className="px-3 py-2.5 w-8">
                  <input type="checkbox" data-testid="bulk-select-all"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer" />
                </th>
              )}
              {['Product', 'SKU', 'Category', 'Sub-category', 'Specs', 'Buy', 'Sell', 'Cost', 'Price', 'Margin', 'Conversions', ''].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr><td colSpan={isAdmin ? 13 : 12} className="text-center py-10 text-sm text-gray-400">No products found</td></tr>
            ) : (
              products.map(p => {
                const margin = p.cost_price > 0 ? ((p.selling_price - p.cost_price) / p.cost_price * 100).toFixed(0) : '—';
                const specs = [p.thickness, p.material, p.grade].filter(Boolean).join(' / ');
                const selected = selectedIds.includes(p.id);
                return (
                  <tr key={p.id} className={`border-b border-gray-50 hover:bg-blue-50/30 transition-colors cursor-pointer ${selected ? 'bg-blue-50/50' : ''}`} onClick={() => openEdit(p)}>
                    {isAdmin && (
                      <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" data-testid={`bulk-select-${p.sku}`}
                          checked={selected}
                          onChange={() => toggleRow(p.id)}
                          className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer" />
                      </td>
                    )}
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-gray-900">{p.name}</p>
                      {p.size && <p className="text-[10px] text-gray-400">{p.size}</p>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-gray-600">{p.sku}</td>
                    <td className="px-3 py-2.5">
                      <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded-full bg-blue-50 text-blue-700">{p.category}</span>
                    </td>
                    <td className="px-3 py-2.5 text-gray-600">
                      {p.sub_category ? (
                        <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded-full bg-purple-50 text-purple-700">{p.sub_category}</span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-gray-500 max-w-[120px] truncate">{specs || '—'}</td>
                    <td className="px-3 py-2.5 text-gray-600">{p.buy_unit || p.unit}</td>
                    <td className="px-3 py-2.5 text-gray-600">{p.sell_unit || p.unit}</td>
                    <td className="px-3 py-2.5 text-gray-600">{p.cost_price ? `₹${p.cost_price.toLocaleString()}` : '—'}</td>
                    <td className="px-3 py-2.5 font-semibold text-gray-900">{p.selling_price ? `₹${p.selling_price.toLocaleString()}` : '—'}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] font-semibold ${Number(margin) > 0 ? 'text-green-600' : 'text-red-500'}`}>{margin}%</span>
                    </td>
                    <td className="px-3 py-2.5">
                      {(p.unit_conversions || []).length > 0 ? (
                        <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded-full bg-purple-50 text-purple-600">{p.unit_conversions.length} rules</span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <button data-testid={`history-product-${p.sku}`} onClick={() => setHistoryProduct({ id: p.id, name: p.name })} className="p-1 rounded hover:bg-blue-50" title="Item History"><History className="w-3 h-3 text-blue-500" /></button>
                        <button data-testid={`edit-product-${p.sku}`} onClick={() => openEdit(p)} className="p-1 rounded hover:bg-gray-100"><Edit2 className="w-3 h-3 text-gray-500" /></button>
                        <button data-testid={`delete-product-${p.sku}`} onClick={() => handleDelete(p.id, p.name)} className="p-1 rounded hover:bg-red-50"><Trash2 className="w-3 h-3 text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />
      </div>

      {/* Floating Bulk Action Bar */}
      {isAdmin && selectedIds.length > 0 && (
        <div data-testid="bulk-action-bar" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-2.5 rounded-full shadow-2xl border border-gray-800"
          style={{ backgroundColor: '#111827', color: '#fff' }}>
          <span className="text-xs font-semibold">{selectedIds.length} selected</span>
          <div className="h-4 w-px bg-gray-600" />
          <button data-testid="bulk-set-subcategory-btn" onClick={openBulkSubCatDialog}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full hover:bg-white/10">
            <Edit2 className="w-3 h-3" /> Set Sub-category
          </button>
          <button data-testid="bulk-clear-btn" onClick={() => setSelectedIds([])}
            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium rounded-full hover:bg-white/10 text-gray-300">
            <X className="w-3 h-3" /> Clear
          </button>
        </div>
      )}

      {/* Bulk Sub-category Modal */}
      {showBulkSubCat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="bulk-subcategory-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Set Sub-category</h3>
              <button onClick={() => setShowBulkSubCat(false)} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">
              Applying to <span className="font-semibold text-gray-800">{selectedIds.length}</span> selected item{selectedIds.length === 1 ? '' : 's'}.
              Leave blank to clear the sub-category.
            </p>
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Sub-category</label>
              <input data-testid="bulk-subcategory-input"
                value={bulkSubCatValue}
                onChange={e => setBulkSubCatValue(e.target.value)}
                list="sub-category-suggestions"
                placeholder="e.g., Signages, Crash Barrier, Cold Plastic"
                autoFocus
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              <datalist id="sub-category-suggestions">
                {subCategorySuggestions.map(s => (
                  <option key={s.sub_category} value={s.sub_category}>{`${s.sub_category} (${s.count})`}</option>
                ))}
              </datalist>
              {subCategorySuggestions.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {subCategorySuggestions.slice(0, 10).map(s => (
                    <button key={s.sub_category} type="button"
                      onClick={() => setBulkSubCatValue(s.sub_category)}
                      className={`px-2 py-0.5 text-[10px] rounded-full border ${bulkSubCatValue === s.sub_category ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {s.sub_category} <span className="text-gray-400">({s.count})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowBulkSubCat(false)} className="px-3 py-1.5 text-[11px] font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button data-testid="bulk-subcategory-apply-btn" onClick={handleBulkSubCatApply} disabled={bulkSubmitting}
                className="flex items-center gap-1.5 px-4 py-1.5 text-[11px] font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
                {bulkSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Apply to {selectedIds.length} item{selectedIds.length === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item History Dialog */}
      {historyProduct && (
        <ItemHistoryDialog
          productId={historyProduct.id}
          productName={historyProduct.name}
          onClose={() => setHistoryProduct(null)}
        />
      )}
    </div>
  );
};

export default ProductMaster;
